import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtMoney } from "@/lib/tournament";
import api, { formatApiError } from "@/lib/api";

export default function ProofReviewDialog({ flag, currency, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const request = async () => {
    setBusy(true);
    try {
      await api.post(`/registrations/${flag.rid}/proof/manual-review`, { note });
      toast.success("Manual review requested — the organizer will check your receipt.");
      setNote(""); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not request review"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!flag} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass" style={{ background: "var(--surface)", color: "var(--text)" }} data-testid="proof-flag-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: "#ef4444" }}><ShieldAlert className="w-5 h-5" /> We couldn't verify this receipt</DialogTitle>
          <DialogDescription style={{ color: "var(--text-2)" }}>Our automatic check found the following:</DialogDescription>
        </DialogHeader>
        <ul className="space-y-1.5 text-sm" data-testid="proof-flag-reasons">
          {(flag?.reasons || []).map((r) => <li key={r} className="rounded-lg px-3 py-2" style={{ background: "rgba(239,68,68,0.08)" }}>{r}</li>)}
        </ul>
        {flag?.expected > 0 && <p className="text-xs" style={{ color: "var(--text-2)" }} data-testid="proof-flag-amounts">Required: <b>{fmtMoney(currency, flag.expected)}</b>{flag.detected != null ? ` · Detected on receipt: ${fmtMoney(currency, flag.detected)}` : ""}</p>}
        <p className="text-xs" style={{ color: "var(--text-2)" }}>If you think the system is wrong, request a manual review. Your application stays <b>unpaid</b> until the organizer finishes checking. Or close this and upload the original receipt.</p>
        <textarea data-testid="proof-review-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500} placeholder="Optional note for the organizer (e.g. reference number)"
          className="w-full rounded-xl bg-surface glow-border px-3 py-2 text-sm outline-none" style={{ color: "var(--text)" }} />
        <DialogFooter className="gap-2">
          <button data-testid="proof-flag-close" onClick={onClose} className="glass rounded-full px-4 py-2 text-sm font-bold" style={{ color: "var(--text)" }}>Upload a different receipt</button>
          <button data-testid="proof-request-review" disabled={busy} onClick={request} className="btn-glow rounded-full px-5 py-2 text-sm font-bold flex items-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Request manual review</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
